/**
 * finalize-content — pure content-assembly for finalizeAssistantMessage.
 *
 * Lifted VERBATIM (byte-for-byte expression sequence) from the body of
 * finalizeAssistantMessage in useChat.ts. This is the side-effect-free part: it
 * takes the raw final text + accumulated turn state and computes:
 *   - finalVisibleContent  (committed to messages state / persisted)
 *   - uiActionsWithAnchors  (forwarded to onUiActions — result.card partitioned out)
 *   - finalQuickActions     (from XAVIOR_ACTIONS extraction)
 *   - resultCards           (serialized into the marker block)
 *   - suppressBubble        (tool-only turn → no bubble)
 *
 * The hook keeps ALL side effects (setStreamingMessage, setMessages,
 * setPendingQuickActions, onUiActions, onMessage, persistence) and the
 * messageFinalized / lastSendRef / meta-merge bookkeeping. Behavior is
 * unchanged: same helper calls in the same order with the same predicates.
 *
 * Pinned by apps/web/src/features/chat/hooks/useChat.finalize-char.test.ts.
 */

import {
  sanitizeHiddenDirectives,
  extractActionsDirective,
} from '../../utils/hidden-directives'
import {
  normalizeAssistantSentenceBoundaries,
  isStrayPageFinalText,
} from '../../utils/stream-text'
import { synthesizeToolSummary } from '@/lib/chat-response-synthesis'
import { RESULT_CARDS_MARKER } from '@features/chat/components/result-cards/types'
import type { ChatUIAction } from '@/lib/api/endpoints/chat'
import type { QuickAction } from '../useChat.types'

export interface AssembleFinalizeContentArgs {
  /** The candidate final text (may be empty / directive-only). */
  finalContent: string
  /** The streamed assistant text accumulated this turn (fallback for finalContent). */
  assistantContent: string
  /** Stable assistant message id used to anchor module.focus actions. */
  assistantId: string
  /** Number of tools used this turn (drives hadActions). */
  toolsUsedCount: number
  /** ui actions passed via meta (queued from the stream / safety nets). */
  metaUiActions: ChatUIAction[]
  /** Tool calls captured this turn (for synthesis fallback). */
  toolCalls: Array<{ name: string; params: unknown }>
  /** Tool results captured this turn (for synthesis fallback). */
  toolResults: Array<Record<string, unknown>>
  /** Live keys of html_viewer modules built this turn (stray-fragment guard). */
  activeHtmlModuleKeys: string[]
}

export interface AssembleFinalizeContentResult {
  finalVisibleContent: string
  uiActionsWithAnchors: ChatUIAction[]
  finalQuickActions: QuickAction[]
  resultCards: unknown[]
  suppressBubble: boolean
}

export function assembleFinalizeContent(
  args: AssembleFinalizeContentArgs,
): AssembleFinalizeContentResult {
  const {
    finalContent,
    assistantContent,
    assistantId,
    toolsUsedCount,
    metaUiActions,
    toolCalls,
    toolResults,
    activeHtmlModuleKeys,
  } = args

  const sanitizedFinal = sanitizeHiddenDirectives(String(finalContent || ''))
  const { cleanContent: contentAfterActions, quickActions: finalQuickActions } =
    extractActionsDirective(sanitizedFinal.content || '')
  const mergedUiActions: ChatUIAction[] = [
    ...(metaUiActions || []),
    ...sanitizedFinal.uiActions,
  ]
  const uiActionsWithAnchorsAll: ChatUIAction[] = mergedUiActions.map((action) => {
    if (!action || action.type !== 'module.focus') return action
    if (typeof action.anchorMessageId === 'string' && action.anchorMessageId.trim()) {
      return action
    }
    return { ...action, anchorMessageId: assistantId }
  })
  // Result-card actions (type === 'result.card') are NOT real UI side
  // effects — they carry per-match card data (e.g. matched emails) that we
  // render inline in the assistant bubble via a trailing marker block.
  // Partition them out so they: (a) get serialized into the message content
  // as RESULT_CARDS_MARKER + JSON, and (b) are NOT forwarded to onUiActions
  // (which only handles genuine module/onboarding directives).
  const resultCardActions = uiActionsWithAnchorsAll.filter(
    (a) => (a as { type?: string })?.type === 'result.card',
  )
  const uiActionsWithAnchors: ChatUIAction[] = uiActionsWithAnchorsAll.filter(
    (a) => (a as { type?: string })?.type !== 'result.card',
  )
  const resultCards = resultCardActions
    .map((a) => (a as { card?: unknown }).card)
    .filter((c) => c != null)
  // Tool-only turn detection: when the agent successfully performs
  // actions (e.g., html.complete that builds a page + emits module.focus)
  // but emits zero visible text, we used to render a literal
  // "No response" bubble — which reads like a failure to the user.
  //
  // New recovery order (commit 4, see docs/pages-diagnostic-2026-04.md):
  //
  //   1. If any stream-authored text survived directive/action
  //      extraction, use it as-is.
  //   2. If the turn succeeded on the tool side but emitted no
  //      user-facing text, synthesise a terse summary from the
  //      tool calls + ui actions ("Updated the page.", "Built X.").
  //   3. If neither is available but we still did *some* action
  //      work (any ui action or tool receipt), suppress the bubble
  //      entirely — the user will see the side-effect (focus,
  //      quick action chips). Do NOT fabricate text.
  //   4. Only when there is no text AND no actions AND no tools do
  //      we fall through to a *transient* error bubble upstream
  //      (layer 3 in the outer catch/finalize path). Never persist
  //      the literal "No response" string.
  let rawText = (
    contentAfterActions.trim() ? contentAfterActions : String(assistantContent || '')
  ).trim()
  // Stray-fragment guard: mirror of the server-side check in chat.ts.
  // When a webpage build's closing sentence gets filtered through the
  // planning-chatter pipe, a trailing adverb like "throughout" or
  // "also" can survive as the only visible text. If we detect that
  // and we know this turn produced HTML, drop it so the synthesis
  // fallback can generate a real summary from the tool calls.
  const htmlModuleEverCreated = activeHtmlModuleKeys.length > 0 ||
    uiActionsWithAnchors.some(a => (a as { type?: string }).type === 'html.init' ||
                                   (a as { type?: string }).type === 'html.complete')
  if (htmlModuleEverCreated && isStrayPageFinalText(rawText)) {
    rawText = ''
  }
  const hadActions = uiActionsWithAnchors.length > 0 || toolsUsedCount > 0
  const synthesised = rawText
    ? ''
    : (synthesizeToolSummary(toolCalls, toolResults, uiActionsWithAnchors) ?? '')
  // Never suppress a turn that produced result cards — the marker content
  // (and the rendered cards) must persist even when the prose is empty.
  const suppressBubble = !rawText && !synthesised && hadActions && resultCards.length === 0

  const visibleProse = suppressBubble
    ? ''
    : rawText
      ? normalizeAssistantSentenceBoundaries(rawText)
      : synthesised
        ? normalizeAssistantSentenceBoundaries(synthesised)
        : ''
  // Append the result-cards marker block to the final, persisted content.
  // splitResultCards() (ChatBox render) strips the marker + JSON before
  // rendering the prose, then renders the cards from the parsed array.
  const finalVisibleContent = resultCards.length > 0
    ? visibleProse + '\n' + RESULT_CARDS_MARKER + JSON.stringify(resultCards)
    : visibleProse

  return { finalVisibleContent, uiActionsWithAnchors, finalQuickActions, resultCards, suppressBubble }
}
